import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { isEqual } from 'lodash';

import { LOCAL_TABLE_STORAGE_KEYS } from 'app/modules/localTableSettings/constants';
import {
  ColumnMetadata,
  LocalColumnConfig,
  LocalTableConfigType,
} from 'app/modules/localTableSettings/models';
import { selectLocalTableConfig } from 'app/modules/localTableSettings/selectors';
import {
  setLocalTableConfig,
  syncLocalTableConfig,
} from 'app/modules/localTableSettings/slice';
import { buildReconciledColumns } from 'app/modules/localTableSettings/utils';
import { useAppDispatch } from 'app/store/storeHooks';

interface Options {
  columns: ColumnMetadata[];
  defaultVisibleKeys: string[];
}

// Returns the reconciled columns from Redux plus a setter that persists
// changes to both Redux and localStorage.
export const useLocalTableConfig = (
  type: LocalTableConfigType,
): [LocalColumnConfig[], (newColumns: LocalColumnConfig[]) => void] => {
  const dispatch = useAppDispatch();
  const columns = useSelector(selectLocalTableConfig(type));

  const setColumns = useCallback(
    (newColumns: LocalColumnConfig[]) => {
      const visibleKeys = newColumns.filter((c) => !c.hidden).map((c) => c.key);
      // Re-reconcile so hidden columns trail alphabetically after save. Matches
      // the order a fresh reload would produce from localStorage.
      const reconciled = buildReconciledColumns(newColumns, visibleKeys);
      dispatch(setLocalTableConfig({ type, columns: reconciled }));
      // Derive keys from the reconciled result so localStorage can't drift from
      // what Redux holds (buildReconciledColumns drops any keys not in `next`).
      const reconciledVisibleKeys = reconciled
        .filter((c) => !c.hidden)
        .map((c) => c.key);
      try {
        localStorage.setItem(
          LOCAL_TABLE_STORAGE_KEYS[type],
          JSON.stringify(reconciledVisibleKeys),
        );
      } catch {}
    },
    [dispatch, type],
  );

  return [columns, setColumns];
};

// Seeds Redux via syncLocalTableConfig whenever the consumer-provided columns change.
// Side-effect only — call useLocalTableConfig separately to read the reconciled state.
export const useSyncLocalTableConfig = (
  type: LocalTableConfigType,
  { columns, defaultVisibleKeys }: Options,
): void => {
  const dispatch = useAppDispatch();
  // Guard against unstable refs from the caller (e.g. a react-query hook that
  // returns a new `data` object each render). Compare content and skip the
  // dispatch if the payload is structurally identical to the last one.
  const prevPayloadRef = useRef<{
    type: LocalTableConfigType;
    columns: ColumnMetadata[];
    defaultVisibleKeys: string[];
  } | null>(null);
  useEffect(() => {
    if (columns.length === 0) {
      return;
    }
    const payload = { type, columns, defaultVisibleKeys };
    if (isEqual(prevPayloadRef.current, payload)) {
      return;
    }
    prevPayloadRef.current = payload;
    dispatch(syncLocalTableConfig(payload));
  }, [type, columns, defaultVisibleKeys, dispatch]);
};
